import type { MouseEvent } from 'react';
import React, { useState } from 'react';
import type {
  DeregisterValidationFn,
  FormBlurEventFn,
  FormChangeEventFn,
  FormValidationResult,
  HasValidationFn,
  RegisterValidationFn,
  SetFormValueFn,
} from '@noths/polaris-client-react-hooks';
import { Button } from '@noths/polaris-design-system-core-components';
import type { Dictionary } from '@noths/polaris-dev-ts-recipes';

import { ErrorMessage } from 'src/components/atoms/ErrorMessage/ErrorMessage';
import { CheckboxGroup } from 'src/components/molecules/CheckboxGroup/CheckboxGroup';
import { RadioButtonGroup } from 'src/components/molecules/RadioButtonGroup/RadioButtonGroup';
import {
  INITIAL_PRODUCT_CATEGORIES,
  YES_NO_DOCUMENTATIONS_OPTIONS,
} from 'src/components/organisms/Signup/constants/aboutYourProductsOptions';
import { REQUIRED_CERTIFICATION_COPY } from 'src/components/organisms/Signup/constants/copy';
import {
  CERT_REQUIREMENTS_TESTID,
  PRODUCT_CATEGORIES_GROUP_TESTID,
} from 'src/components/organisms/Signup/constants/testIds';
import type { FormValidationErrors } from 'src/components/organisms/Signup/types';
import { ABOUT_YOUR_PRODUCT_VALIDATION_RULE_NAMES } from 'src/components/organisms/Signup/validation';
import * as styles from 'src/styles/styles';
import type { RegistrationForm } from 'src/types/registration';
import { Products } from './Products';

const getDynamicProductFieldName = (id: string) => `products.${id}.url`;

const getValidatableFields = (form: RegistrationForm) => {
  const onlineStoreFields = form.products.map(({ id }) => getDynamicProductFieldName(id));

  return [...ABOUT_YOUR_PRODUCT_VALIDATION_RULE_NAMES, ...onlineStoreFields];
};

export interface AboutYourProductsProps {
  deregisterValidation: DeregisterValidationFn;
  errors: FormValidationErrors;
  formValidator: (subsectionValidationRuleNames?: string[]) => FormValidationResult<Dictionary>;
  formValues: RegistrationForm;
  handleSubmit: (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  hasValidation: HasValidationFn;
  onBlur: FormBlurEventFn;
  onFormChange: FormChangeEventFn;
  pageBack: () => boolean;
  registerValidation: RegisterValidationFn<RegistrationForm>;
  setError: (errorName: string, errorMessage: string) => void;
  setFormValue: SetFormValueFn<RegistrationForm>;
}

export const AboutYourProducts = ({
  deregisterValidation,
  errors,
  formValidator,
  formValues,
  handleSubmit,
  hasValidation,
  onBlur,
  pageBack,
  registerValidation,
  setError,
  setFormValue,
}: AboutYourProductsProps) => {
  const formContainsErrors = Boolean(Object.keys(errors).length);

  const [productCategories] = useState(INITIAL_PRODUCT_CATEGORIES);

  const handleClickBack = () => {
    const { isValid } = formValidator(getValidatableFields(formValues));
    if (isValid) {
      pageBack();
    }
  };

  const handleFormSubmit = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    const { isValid } = formValidator(getValidatableFields(formValues));
    if (isValid) {
      handleSubmit(e);
    }
  };

  return (
    <section css={styles.pageSection}>
      <h2 css={styles.sectionHeading}>Section 3</h2>
      <h3 css={styles.sectionTitle}>Tell us about your products</h3>
      <div css={styles.subSection}>
        <CheckboxGroup
          accessibleLabel="Select one or more of the following categories that apply to your product range"
          additionalCss={[
            styles.grouped,
            styles.makeTwoColCheckboxGrid(1 + Math.floor(productCategories.length / 2)),
          ]}
          checkboxData={productCategories}
          data-testid={PRODUCT_CATEGORIES_GROUP_TESTID}
          error={errors.productCategories as string | undefined}
          name="productCategories"
          onBlur={onBlur}
          onChange={(value, name) =>
            setFormValue(
              name as keyof typeof formValues,
              value as (typeof formValues)[keyof typeof formValues],
            )
          }
          required
          value={formValues.productCategories}
        />
      </div>
      <div css={styles.subSection}>
        <RadioButtonGroup
          accessibleLabel={REQUIRED_CERTIFICATION_COPY}
          additionalCss={styles.grouped}
          data-testid={CERT_REQUIREMENTS_TESTID}
          error={errors['certification.required'] as string | undefined}
          name="certification.required"
          onBlur={onBlur}
          onChange={(value) =>
            setFormValue('certification.required' as keyof RegistrationForm, value)
          }
          radioButtonData={YES_NO_DOCUMENTATIONS_OPTIONS}
          required
          value={formValues.certification.required}
        />
      </div>
      <div css={styles.subSection}>
        <Products
          data-position="3"
          data-section="3"
          deregisterValidation={deregisterValidation}
          errors={errors}
          formValues={formValues}
          hasValidation={hasValidation}
          onBlur={onBlur}
          registerValidation={registerValidation}
          setError={setError}
          setFormValue={setFormValue}
        />
      </div>
      {Object.keys(errors).length > 0 && <ErrorMessage />}
      <div css={styles.navigationButtonsGrid}>
        <Button
          disabled={formContainsErrors}
          id="back-from-page-about-your-products"
          onClick={handleClickBack}
          secondary
        >
          Back
        </Button>
        <Button disabled={formContainsErrors} onClick={handleFormSubmit}>
          Submit
        </Button>
      </div>
    </section>
  );
};
